@file:Suppress("MagicNumber", "LongMethod")

package com.nonopn.skydome.common.app.pages.pdf

import com.nonopn.skydome.common.app.pages.certnsg.CertNsgModel
import com.nonopn.skydome.common.app.pages.keraunic.KeraunicDailyActivity
import com.nonopn.skydome.common.app.pages.keraunic.KeraunicMonthlyActivity
import com.nonopn.skydome.common.app.pages.keraunic.KeraunicReport
import com.nonopn.skydome.common.app.pages.main.model.api.Project
import com.nonopn.skydome.common.app.utils.fmt
import com.nonopn.skywatcher.maps.MapUtils
import korlibs.time.DateTime
import kotlin.math.abs
import kotlin.math.absoluteValue
import kotlin.math.roundToInt

// Generates self-contained HTML (CSS embedded, logo base64, no network calls).
// Output is meant to be opened in a browser / WebView then saved as PDF.
object PdfHtmlBuilder {

    // ─────────────────────────────────────────────────────────────────────────
    // Public API
    // ─────────────────────────────────────────────────────────────────────────

    fun buildKeraunicReport(
        project: Project,
        report: KeraunicReport,
        observations: String,
        title: String,
        lang: PdfOutputLang,
        generatedDate: String,
    ): String {
        val s = PdfTranslations.get(lang)
        val stats = report.stats
        val posCount = stats.polarity.positiveCount
        val negCount = stats.polarity.negativeCount
        val total = stats.totalStrikes
        val nsgFmt = stats.nsgPerKm2Year.fmt(2)
        val maxKaInt = stats.maxIntensityKa.absoluteValue.roundToInt()
        val avgKaFmt = stats.avgIntensityKa.absoluteValue.fmt(1)
        val posPct = if (total > 0) (posCount * 100.0 / total).roundToInt() else 0
        val negPct = 100 - posPct
        val dateFrom = epochToShortDate(report.dateRange.fromSeconds)
        val dateTo = epochToShortDate(report.dateRange.toSeconds)

        return buildHtml(
            lang = lang.code,
            title = title,
            docTitle = s.docTitleKeraunic,
            docSubtitle = "${s.period} : $dateFrom \u2192 $dateTo",
            generatedBy = s.generatedBy,
            generatedOn = "${s.generatedOn} $generatedDate",
            network = s.network,
            body = buildString {
                // Site info
                append(siteInfoCard(s, project))

                // KPIs
                append(
                    kpisCard(
                        s = s,
                        total = total,
                        maxKa = maxKaInt,
                        avgKa = avgKaFmt,
                        nsg = nsgFmt
                    )
                )

                // Monthly histogram
                append(monthlyHistogramCard(s, report.monthlyActivity))

                // Polarity
                append(polarityCard(s, posCount, negCount, posPct, negPct))

                // Top 5 stormy days
                append(topDaysCard(s, report.topStormyDays))

                // Conclusion
                append(
                    conclusionCard(
                        s = s,
                        text = s.keraunicConclusionText(
                            project.name, total, nsgFmt, maxKaInt, posCount, negCount
                        ),
                        extraNorms = listOf("IEC 62858:2019", "IEC 62305-2:2024")
                    )
                )

                // Observations
                if (observations.isNotBlank()) append(observationsCard(s, observations))
            }
        )
    }

    /**
     * Data holder for zone statistics (0–1 km or 1–2 km).
     * All intensities in kA (already divided by 1000).
     */
    // ─────────────────────────────────────────────────────────────────────────
    // Cert Foudroiement — matches validated template preview_cert_foudroiement.html
    // ─────────────────────────────────────────────────────────────────────────

    /**
     * Questionnaire site-type options for cert foudroiement / keraunic report.
     */
    val SITE_TYPES_FR = listOf(
        "Établissement public (école, hôpital…)",
        "Site industriel / entrepôt",
        "Infrastructure critique (énergie, eau, télécom)",
        "Site tertiaire (bureaux, commerce)",
        "Site agricole / outdoor",
        "Autre"
    )
    val PRESENCE_OPTIONS_FR = listOf("Permanente", "Horaire de travail", "Occasionnelle", "Aucune")
    val OBJECTIVE_OPTIONS_FR = listOf(
        "Audit interne", "Dossier assurance", "Conformité réglementaire", "Investissement protection"
    )
    val DAMAGE_TYPES_FR = listOf(
        "Incendie / feu",
        "Destruction d'équipement électrique",
        "Perturbation automate / SCADA / informatique",
        "Coupure d'alimentation électrique",
        "Blessure ou risque vital pour une personne",
        "Dommage structurel au bâtiment",
        "Autre type de dommage"
    )

    @Suppress("LongParameterList", "LongMethod")
    fun buildCertFoudr(
        lang: PdfOutputLang,
        projectName: String,
        projectLat: Double,
        projectLon: Double,
        impactLat: Double,
        impactLon: Double,
        impactTimestamp: Long,
        impactAmplitude: Int,
        impactPercent: Double,
        impactExternalId: String,
        sensorCount: Int = 0,
        questionnaire: PdfQuestionnaireData? = null,
        generatedDate: String = "",
    ): String {
        val t = StrikeCertificateTranslations.forLanguage(lang)
        val absKa = abs(impactAmplitude.toDouble()) / 1000.0
        val isPositive = impactAmplitude > 0
        val polarity = if (isPositive) "CG+" else "CG\u2212"
        val distKm = MapUtils.distance(
            projectLat, projectLon, impactLat, impactLon
        )
        val dt = DateTime.fromUnixMillis(impactTimestamp * 1000L).local
        val dateLabel = "${dt.dayOfMonth.toString().padStart(2, '0')}/" +
            "${dt.month1.toString().padStart(2, '0')}/${dt.yearInt}"
        val timeLabel = "${dt.hours.toString().padStart(2, '0')}:" +
            "${dt.minutes.toString().padStart(2, '0')}:${dt.seconds.toString().padStart(2, '0')}"
        val kaFmt = absKa.fmt(1)
        val distFmt = if (distKm < 1.0) "${(distKm * 1000).toInt()} m" else distKm.fmt(1) + " km"
        val pctFmt = impactPercent.fmt(1)
        val siteName = questionnaire?.siteName?.ifBlank { projectName } ?: projectName
        val siteType = questionnaire?.siteType ?: ""
        val presence = questionnaire?.humanPresence ?: ""
        val objective = questionnaire?.objective ?: ""
        val siteTypeDisplay = t.siteType(siteType)
        val presenceDisplay = t.presence(presence)
        val objectiveDisplay = t.objective(objective)
        val hasRod = questionnaire?.hasLightningRod ?: false
        val hasSurge = questionnaire?.hasSurgeProtection ?: false
        val incident = questionnaire?.incident

        // Probability level
        val probaClass = when {
            impactPercent >= 80 -> "high"
            impactPercent >= 30 -> ""
            else -> "low"
        }
        // Which incidence card is active
        val isDirectActive = impactPercent >= 80
        val isIndirectActive = impactPercent in 30.0..79.99

        val genDate = generatedDate.ifBlank { dateLabel }
        val latDir = if (projectLat >= 0) "N" else "S"
        val lonDir = if (projectLon >= 0) "E" else "O"
        val latFmt = abs(projectLat).fmt(4)
        val lonFmt = abs(projectLon).fmt(4)
        val coordStr = "$latFmt° $latDir · $lonFmt° $lonDir"
        val metaParts = mutableListOf(coordStr)
        if (siteType.isNotBlank()) metaParts.add(siteTypeDisplay)
        if (presence.isNotBlank()) metaParts.add(presenceDisplay)
        if (objective.isNotBlank()) metaParts.add(objectiveDisplay)
        val siteMeta = metaParts.joinToString(" · ")

        return buildString {
            append(certFoudrHtmlHead(lang, t.documentTitle))
            if (questionnaire != null) {
                append(
                    certFoudrPageQ(
                        siteName, siteType, presence, hasRod, hasSurge, objective, incident, t
                    )
                )
            }
            append(
                certFoudrPage1(
                    siteName, siteMeta, projectLat, projectLon,
                    impactLat, impactLon, distFmt,
                    impactExternalId, "$dateLabel · $timeLabel", distFmt,
                    kaFmt, polarity, sensorCount, pctFmt, probaClass,
                    incident, genDate, lang, t
                )
            )
            append(
                certFoudrPage2(
                    siteName, siteMeta, impactExternalId, pctFmt,
                    isDirectActive, isIndirectActive, impactPercent,
                    siteType, presence, hasRod, hasSurge, objective,
                    kaFmt, polarity, genDate, t
                )
            )
            append("\n</body>\n</html>")
        }
    }

    data class ZoneStatsPdf(
        val count: Int,
        val minKa: Double,
        val medianKa: Double,
        val avgKa: Double,
        val maxKa: Double,
    )

    /**
     * Cert Nsg — standalone HTML matching the validated template
     * (preview_cert_nsg.html). Self-contained: own CSS, logo, footer.
     */
    fun buildCertNsg(
        project: Project,
        model: CertNsgModel,
        lang: PdfOutputLang,
        generatedDate: String,
        periodLabel: String = "",
        zone1Stats: ZoneStatsPdf? = null,
        zone2Stats: ZoneStatsPdf? = null,
    ): String {
        val nsgFmt = model.nsgFormatted
        val latFmt = project.latitude.fmt(4)
        val lonFmt = project.longitude.fmt(4)
        val barW = (model.nsg12Months / 4.0 * 100).coerceIn(10.0, 95.0).roundToInt()
        val safeName = project.name.replace("&", "&amp;").replace("<", "&lt;")
            .replace(">", "&gt;").replace("\"", "&quot;")
        val l = certNsgLabels(lang)
        return """<!DOCTYPE html>
<html lang="${lang.code}"><head><meta charset="UTF-8"><style>
@page{size:A4 portrait;margin:0}*{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Segoe UI',Arial,sans-serif;background:#fff;color:#1A2B4A;font-size:15px;
  -webkit-print-color-adjust:exact;print-color-adjust:exact}
.page{width:210mm;min-height:297mm;position:relative;padding:0 0 80px 0;background:#fff}
.hb{background:#fff;border-bottom:2px solid #2B7FE0;padding:18px 32px;display:flex;
  align-items:center;justify-content:space-between}
.hb img{height:52px;max-width:200px;object-fit:contain}
.ht{color:#1A2B4A;font-size:17px;font-weight:700;letter-spacing:1px;text-transform:uppercase;text-align:right}
.hs{color:#2B7FE0;font-size:13px;font-weight:400;margin-top:3px}
.bd{padding:24px 32px 0 32px}
.sn{text-align:center;margin-bottom:22px}
.sn h2{font-size:22px;font-weight:700;color:#1A2B4A}
.sn p{font-size:13px;color:#666;margin-top:4px;letter-spacing:.5px}
.st{font-size:13px;font-weight:700;color:#2B7FE0;text-transform:uppercase;letter-spacing:1px;
  margin-bottom:12px;padding-bottom:4px;border-bottom:2px solid #2B7FE0}
.nh{text-align:center;margin-bottom:22px;padding:20px;background:linear-gradient(135deg,#F5F7FA,#EEF2F8);
  border-radius:12px;border:1.5px solid #D0D8E8}
.nl{font-size:13px;color:#888;text-transform:uppercase;letter-spacing:1px}
.nv{font-size:56px;font-weight:900;color:#2B7FE0;line-height:1.1}
.nu{font-size:20px;color:#555;font-weight:400}
.np{font-size:14px;color:#888;margin-top:6px}
.bb{margin-bottom:22px}
.br{display:flex;align-items:center;margin-bottom:8px;gap:12px}
.bl{width:130px;font-size:14px;color:#555;flex-shrink:0}
.bw{flex:1;background:#EEF2F8;border-radius:6px;height:28px;overflow:hidden}
.bf{height:100%;border-radius:6px;display:flex;align-items:center;padding-left:10px}
.bf span{font-size:13px;font-weight:700;color:#fff;white-space:nowrap}
.bg{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:22px}
.cc{background:#F5F7FA;border-radius:10px;padding:14px 16px;border-left:4px solid #2B7FE0}
.cz{font-size:13px;font-weight:700;color:#2B7FE0;text-transform:uppercase;letter-spacing:.5px;margin-bottom:10px}
.cr{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:6px}
.cr label{font-size:13px;color:#888}
.cr .cv{font-size:15px;font-weight:700;color:#1A2B4A}
.fb{display:inline-block;background:#E8F5E9;border:1.5px solid #2E7D32;color:#1B5E20;
  border-radius:20px;padding:5px 14px;font-size:13px;font-weight:700;margin-top:8px}
.mb{background:#F5F7FA;border-radius:8px;padding:14px 16px;font-size:13px;color:#555;
  line-height:1.6;margin-bottom:16px}
.mb strong,.nb strong{color:#1A2B4A}
.nb{background:#F5F7FA;border-radius:8px;padding:12px 16px;font-size:13px;color:#555;line-height:1.6}
.vb{background:#EEF2F8;border-radius:8px;padding:10px 16px;margin-bottom:14px;font-size:13px;color:#555;line-height:1.6}
.ft{position:absolute;bottom:20px;left:32px;right:32px;border-top:1px solid #D0D8E8;
  padding-top:8px;display:flex;justify-content:space-between;align-items:center;font-size:12px;color:#888}
</style></head><body>
<div class="page">
  <div class="hb">
    <img src="data:image/png;base64,$LOGO_B64" alt="Strike Radar">
    <div class="ht">${l.title}<div class="hs">Nsg — GLD360+</div></div>
  </div>
  <div class="bd">
    <div class="sn" style="margin-top:18px">
      <h2>$safeName</h2>
      <p>$latFmt° N · $lonFmt° E &nbsp;|&nbsp; $periodLabel &nbsp;|&nbsp; $generatedDate</p>
    </div>
    <div class="st">Localisation</div>
${SvgMapBuilder.nsgGridMap(project.latitude, project.longitude, safeName, lang = lang)}
    <div class="st">${l.measuredTitle}</div>
    <div class="nh">
      <div class="nl">${l.measuredLabel}</div>
      <div class="nv">$nsgFmt <span class="nu">${l.unit}</span></div>
      <div class="np">Zone 0–2 km · $periodLabel · GLD360+</div>
    </div>
    <div class="st">${l.compTitle}</div>
    <div class="bb">
      <div class="br"><div class="bl">${l.low}</div>
        <div class="bw"><div class="bf" style="background:#4CAF50;width:25%"><span>&lt; 1 ${l.unit}</span></div></div></div>
      <div class="br"><div class="bl">${l.moderate}</div>
        <div class="bw"><div class="bf" style="background:#8BC34A;width:45%"><span>1 – 3 ${l.unit}</span></div></div></div>
      <div class="br"><div class="bl" style="color:#2B7FE0;font-weight:700">⚡ ${l.yourSite}</div>
        <div class="bw"><div class="bf" style="background:#2B7FE0;width:$barW%;border:3px solid #1A2B4A"><span>Nsg = $nsgFmt</span></div></div>
        <div style="width:80px;font-size:13px;color:#2B7FE0;font-weight:700;text-align:right">$nsgFmt</div></div>
      <div class="br"><div class="bl">${l.high}</div>
        <div class="bw"><div class="bf" style="background:#E63B2E;width:100%"><span>&gt; 3 ${l.unit}</span></div></div></div>
    </div>
${zoneStatsBlock(l, zone1Stats, zone2Stats)}
    <div class="mb">${l.methodoNote}</div>
    <div class="vb"><strong style="color:#1A2B4A">📌 Nsg vs Ng</strong><br>${l.nsgVsNg}</div>
    <div class="nb"><strong>${l.normsTitle}</strong><br>${l.normsText}</div>
  </div>
  <div class="ft">
    <span>Strike Radar · ${l.title}</span>
    <span>$generatedDate · 1 / 1</span>
  </div>
</div>
</body></html>"""
    }

    private data class CertNsgLabels(
        val title: String, val measuredTitle: String, val measuredLabel: String,
        val unit: String, val compTitle: String, val low: String, val moderate: String,
        val high: String, val yourSite: String, val zoneTitle: String,
        val zone1: String, val zone2: String, val detected: String, val minI: String,
        val medianI: String, val avgI: String, val maxI: String, val reliability: String,
        val methodoNote: String, val nsgVsNg: String, val normsTitle: String, val normsText: String,
    )

    private fun certNsgLabels(lang: PdfOutputLang) = when (lang) {
        PdfOutputLang.EN -> CertNsgLabels(
            title = "Ground Flash Density Certificate", measuredTitle = "Measured ground flash density (Nsg)",
            measuredLabel = "Nsg measured over the period", unit = "strikes/km²/year",
            compTitle = "Comparison — Reference scale", low = "Low", moderate = "Moderate",
            high = "High", yourSite = "Your site", zoneTitle = "GLD360+ sensor data — Zones 0–1 km and 1–2 km",
            zone1 = "Zone 0 – 1 km", zone2 = "Zone 1 – 2 km", detected = "Detected strikes",
            minI = "Min. intensity", medianI = "Median intensity", avgI = "Mean intensity",
            maxI = "Max. intensity", reliability = "✓ High reliability (≥ 5 sensors)",
            methodoNote = "Nsg is calculated from the number of strikes detected within 0–2 km of the site over " +
                "the analysis period, expressed as an annual density per km². A higher number of contributing " +
                "sensors improves impact location accuracy.",
            nsgVsNg = "<strong>Nsg</strong> (ground strikes per km²/year) is the normative quantity used for " +
                "lightning risk assessment. It differs from <strong>Ng</strong> (total density including " +
                "intra-cloud arcs). The <strong>GLD360+</strong> network directly provides filtered Nsg — " +
                "ground strikes only — ensuring relevance for risk analysis.",
            normsTitle = "Normative references:",
            normsText = "IEC 62305-2:2024 — Lightning risk assessment (Nsg quantity used in compliance with this standard)<br>" +
                "IEC 62858:2019 — Lightning ground flash density from detection networks (Nsg)<br>" +
                "Strike detected by the <strong>GLD360+</strong> network (real-time detection, worldwide coverage).",
        )
        PdfOutputLang.ES -> CertNsgLabels(
            title = "Certificado de Densidad de Rayos", measuredTitle = "Densidad de rayos medida (Nsg)",
            measuredLabel = "Nsg medido en el período", unit = "impactos/km²/año",
            compTitle = "Comparación — Escala de referencia", low = "Baja", moderate = "Moderada",
            high = "Alta", yourSite = "Su sitio", zoneTitle = "Datos sensores GLD360+ — Zonas 0–1 km y 1–2 km",
            zone1 = "Zona 0 – 1 km", zone2 = "Zona 1 – 2 km", detected = "Impactos detectados",
            minI = "Intensidad mín.", medianI = "Intensidad mediana", avgI = "Intensidad media",
            maxI = "Intensidad máx.", reliability = "✓ Alta fiabilidad (≥ 5 sensores)",
            methodoNote = "El Nsg se calcula a partir del número de impactos detectados en la zona 0–2 km " +
                "del sitio durante el período de análisis, expresado como densidad anual por km².",
            nsgVsNg = "El <strong>Nsg</strong> (rayos al suelo por km²/año) es la magnitud normativa " +
                "utilizada para la evaluación del riesgo de rayos. La red <strong>GLD360+</strong> proporciona " +
                "directamente el Nsg filtrado — solo rayos al suelo.",
            normsTitle = "Referencias normativas:",
            normsText = "IEC 62305-2:2024 — Evaluación del riesgo de rayos (magnitud Nsg utilizada conforme a esta norma)<br>" +
                "IEC 62858:2019 — Densidad de rayos por red de detección (Nsg)<br>" +
                "Impacto detectado por la red <strong>GLD360+</strong> (detección en tiempo real, cobertura mundial).",
        )
        PdfOutputLang.PT -> CertNsgLabels(
            title = "Certificado de Densidade de Raios", measuredTitle = "Densidade de raios medida (Nsg)",
            measuredLabel = "Nsg medido no período", unit = "impactos/km²/ano",
            compTitle = "Comparação — Escala de referência", low = "Baixa", moderate = "Moderada",
            high = "Alta", yourSite = "Seu site", zoneTitle = "Dados sensores GLD360+ — Zonas 0–1 km e 1–2 km",
            zone1 = "Zona 0 – 1 km", zone2 = "Zona 1 – 2 km", detected = "Impactos detectados",
            minI = "Intensidade mín.", medianI = "Intensidade mediana", avgI = "Intensidade média",
            maxI = "Intensidade máx.", reliability = "✓ Alta fiabilidade (≥ 5 sensores)",
            methodoNote = "O Nsg é calculado a partir do número de impactos detectados na zona 0–2 km " +
                "do site durante o período de análise, expresso como densidade anual por km².",
            nsgVsNg = "O <strong>Nsg</strong> (raios ao solo por km²/ano) é a grandeza normativa " +
                "utilizada para avaliação do risco de raios. A rede <strong>GLD360+</strong> fornece " +
                "diretamente o Nsg filtrado — apenas raios ao solo.",
            normsTitle = "Referências normativas:",
            normsText = "IEC 62305-2:2024 — Avaliação do risco de raios (grandeza Nsg utilizada em conformidade com esta norma)<br>" +
                "IEC 62858:2019 — Densidade de raios por rede de deteção (Nsg)<br>" +
                "Impacto detetado pela rede <strong>GLD360+</strong> (deteção em tempo real, cobertura mundial).",
        )
        else -> CertNsgLabels( // FR
            title = "Certificat de Densité de Foudroiement", measuredTitle = "Densité de foudroiement mesurée (Nsg)",
            measuredLabel = "Nsg mesuré sur la période", unit = "impacts/km²/an",
            compTitle = "Comparaison — Barème de référence", low = "Faible", moderate = "Modérée",
            high = "Élevée", yourSite = "Votre site", zoneTitle = "Données capteurs GLD360+ — Zones 0–1 km et 1–2 km",
            zone1 = "Zone 0 – 1 km", zone2 = "Zone 1 – 2 km", detected = "Impacts détectés",
            minI = "Intensité min.", medianI = "Intensité médiane", avgI = "Intensité moyenne",
            maxI = "Intensité max.", reliability = "✓ Fiabilité élevée (≥ 5 capteurs)",
            methodoNote = "Le Nsg est calculé à partir du nombre d'impacts détectés dans la zone 0–2 km du site " +
                "sur la période d'analyse, ramené à une densité annuelle par km². Un nombre élevé de capteurs " +
                "contributeurs améliore la précision de localisation des impacts.",
            nsgVsNg = "Le <strong>Nsg</strong> (Nombre de coups de foudre au sol par km²/an) est la grandeur " +
                "normative utilisée pour l'évaluation du risque foudre. Il diffère du <strong>Ng</strong> " +
                "(densité totale incluant les arcs intra-nuages). Le réseau <strong>GLD360+</strong> fournit " +
                "directement le Nsg filtré — coups de foudre sol uniquement — ce qui garantit la pertinence " +
                "de cette valeur pour l'analyse de risque.",
            normsTitle = "Références normatives :",
            normsText = "IEC 62305-2:2024 — Évaluation du risque foudre (grandeur Nsg utilisée conformément " +
                "à cette norme)<br>IEC 62858:2019 — Densité de foudroiement par réseau de détection (Nsg)<br>" +
                "Impact détecté par le réseau <strong>GLD360+</strong> (détection temps réel, couverture mondiale).",
        )
    }

    private fun zoneStatsBlock(l: CertNsgLabels, z1: ZoneStatsPdf?, z2: ZoneStatsPdf?): String {
        if (z1 == null && z2 == null) return ""
        fun card(label: String, z: ZoneStatsPdf?) = if (z == null) "" else """
      <div class="cc"><div class="cz">$label</div>
        <div class="cr"><label>${l.detected}</label><div class="cv">${z.count}</div></div>
        <div class="cr"><label>${l.minI}</label><div class="cv">${z.minKa.fmt(1)} kA</div></div>
        <div class="cr"><label>${l.medianI}</label><div class="cv">${z.medianKa.fmt(1)} kA</div></div>
        <div class="cr"><label>${l.avgI}</label><div class="cv">${z.avgKa.fmt(1)} kA</div></div>
        <div class="cr"><label>${l.maxI}</label><div class="cv">${z.maxKa.fmt(1)} kA</div></div>
        <div class="fb">${l.reliability}</div>
      </div>"""
        return """    <div class="st">${l.zoneTitle}</div>
    <div class="bg">${card(l.zone1, z1)}${card(l.zone2, z2)}</div>
"""
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Card builders
    // ─────────────────────────────────────────────────────────────────────────

    private fun siteInfoCard(s: PdfStrings, project: Project) = """
        <div class="card">
          <div class="card-title">${s.site}</div>
          <div class="site-info">
            <div class="info-row"><span class="info-label">${s.site}</span>
              <span class="info-value">${project.name}</span></div>
            <div class="info-row"><span class="info-label">${s.latitude}</span>
              <span class="info-value">${project.latitude.fmt(5)}&deg;</span></div>
            <div class="info-row"><span class="info-label">${s.longitude}</span>
              <span class="info-value">${project.longitude.fmt(5)}&deg;</span></div>
          </div>
        </div>
    """.trimIndent()

    private fun kpisCard(
        s: PdfStrings,
        total: Int,
        maxKa: Int,
        avgKa: String,
        nsg: String
    ) = """
        <div class="card">
          <div class="card-title">${s.totalStrikes}</div>
          <div class="kpi-row">
            <div class="kpi">
              <div class="kpi-value" style="color:#2B7FE0">${formatThousands(total)}</div>
              <div class="kpi-unit">${s.impacts}</div>
              <div class="kpi-label">${s.totalStrikes}</div>
            </div>
            <div class="kpi">
              <div class="kpi-value" style="color:#E63B2E">$maxKa</div>
              <div class="kpi-unit">kA</div>
              <div class="kpi-label">${s.maxIntensity}</div>
            </div>
            <div class="kpi">
              <div class="kpi-value" style="color:#1A2B4A">$avgKa</div>
              <div class="kpi-unit">kA</div>
              <div class="kpi-label">${s.avgIntensity}</div>
            </div>
            <div class="kpi">
              <div class="kpi-value" style="color:#2B7FE0">$nsg</div>
              <div class="kpi-unit">${s.nsgUnit}</div>
              <div class="kpi-label">${s.nsgLabel}</div>
            </div>
          </div>
        </div>
    """.trimIndent()

    private fun monthlyHistogramCard(s: PdfStrings, data: List<KeraunicMonthlyActivity>): String {
        val maxVal = data.maxOfOrNull { it.count }?.coerceAtLeast(1) ?: 1
        val bars = data.joinToString("\n") { month ->
            val pct = (month.count.toFloat() / maxVal * 100).roundToInt().coerceIn(2, 100)
            val label = s.months.getOrElse(month.month - 1) { "${month.month}" }
            """
            <div class="bar-col">
              <div class="bar" style="height:${pct}%">
                <span class="bar-count">${if (month.count > 0) "${month.count}" else ""}</span>
              </div>
              <div class="bar-label">$label</div>
            </div>
            """.trimIndent()
        }
        return """
            <div class="card">
              <div class="card-title">${s.monthlyActivity}</div>
              <div class="histogram">$bars</div>
            </div>
        """.trimIndent()
    }

    private fun polarityCard(
        s: PdfStrings,
        posCount: Int,
        negCount: Int,
        posPct: Int,
        negPct: Int
    ) = """
        <div class="card">
          <div class="card-title">${s.polarityBreakdown}</div>
          <div class="polarity-row">
            <div>
              <div class="polarity-box polarity-neg">
                <div class="polarity-count">${formatThousands(negCount)}</div>
                <div class="polarity-label">${s.impacts}</div>
              </div>
              <div class="polarity-title" style="color:#2B7FE0">CG&minus;</div>
              <div class="polarity-pct">$negPct%</div>
            </div>
            <div>
              <div class="polarity-box polarity-pos">
                <div class="polarity-count">${formatThousands(posCount)}</div>
                <div class="polarity-label">${s.impacts}</div>
              </div>
              <div class="polarity-title" style="color:#E63B2E">CG+</div>
              <div class="polarity-pct">$posPct%</div>
            </div>
          </div>
        </div>
    """.trimIndent()

    private fun topDaysCard(
        s: PdfStrings,
        days: List<KeraunicDailyActivity>
    ) = buildString {
        append("""<div class="card"><div class="card-title">${s.topStormyDays}</div>""")
        append("""<table><thead><tr>""")
        append("""<th>${s.rank}</th><th>Date</th><th>${s.count}</th><th>${s.maxKa}</th>""")
        append("""</tr></thead><tbody>""")
        days.forEachIndexed { i, d ->
            append(
                """<tr>
                  <td>${i + 1}</td>
                  <td>${d.dateIso}</td>
                  <td><b>${formatThousands(d.count)}</b></td>
                  <td style="color:#E63B2E">${d.maxIntensityKa.absoluteValue.roundToInt()}</td>
                </tr>"""
            )
        }
        append("""</tbody></table></div>""")
    }

    private fun conclusionCard(
        s: PdfStrings,
        text: String,
        extraNorms: List<String>
    ) = buildString {
        append("""<div class="conclusion"><div class="conclusion-title">📋 ${s.normativeConclusion}</div>""")
        append("""<div class="conclusion-text">$text</div>""")
        append("<div>")
        extraNorms.forEach { append("""<span class="norm-badge">$it</span>""") }
        append("</div></div>")
    }

    private fun observationsCard(s: PdfStrings, text: String) = """
        <div class="card">
          <div class="card-title">📝 ${s.freeObservations}</div>
          <div class="observations">$text</div>
        </div>
    """.trimIndent()

    // ─────────────────────────────────────────────────────────────────────────
    // HTML shell
    // ─────────────────────────────────────────────────────────────────────────

    private fun buildHtml(
        lang: String,
        title: String,
        docTitle: String,
        docSubtitle: String,
        generatedBy: String,
        generatedOn: String,
        network: String,
        body: String,
    ) = """<!DOCTYPE html>
<html lang="$lang">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<title>$title</title>
<style>
${css()}
</style>
</head>
<body>
<div class="container">
  <div class="header">
    <div>
      <div class="doc-title">$docTitle</div>
      <div class="doc-subtitle">$docSubtitle</div>
    </div>
    <img class="logo" src="data:image/png;base64,$LOGO_B64" alt="Strike Radar">
  </div>
  $body
  <div class="footer">
    <strong>$generatedBy</strong> &middot; $generatedOn &middot; $network<br>
    IEC 62305-2:2024 &middot; IEC 62858:2019 &middot; NF C 17-102:2011
  </div>
</div>
</body>
</html>"""

    // ─────────────────────────────────────────────────────────────────────────
    // Helpers
    // ─────────────────────────────────────────────────────────────────────────

    private fun formatThousands(n: Int): String {
        val s = n.toString()
        val result = StringBuilder()
        s.reversed().forEachIndexed { i, c ->
            if (i > 0 && i % 3 == 0) result.append('\u202F') // narrow no-break space
            result.append(c)
        }
        return result.reverse().toString()
    }

    private fun epochToShortDate(epochSeconds: Long): String {
        if (epochSeconds < 946684800L) return "\u2013" // before year 2000 = invalid
        val days = (epochSeconds / 86400).toInt()
        val year = daysToYear(days)
        val dayOfYear = days - yearToDays(year)
        val month = dayOfYearToMonth(dayOfYear, isLeapYear(year))
        return "$year-${month.toString().padStart(2, '0')}"
    }

    private fun daysToYear(days: Int): Int {
        var y = 1970
        var remaining = days
        while (true) {
            val diy = if (isLeapYear(y)) 366 else 365
            if (remaining < diy) return y
            remaining -= diy
            y++
        }
    }

    private fun yearToDays(year: Int): Int {
        var d = 0
        for (y in 1970 until year) d += if (isLeapYear(y)) 366 else 365
        return d
    }

    private fun isLeapYear(y: Int) = (y % 4 == 0 && y % 100 != 0) || y % 400 == 0

    private fun dayOfYearToMonth(doy: Int, leap: Boolean): Int {
        val dims = intArrayOf(31, if (leap) 29 else 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31)
        var rem = doy
        for ((i, d) in dims.withIndex()) {
            if (rem < d) return i + 1
            rem -= d
        }
        return 12
    }

    // ─────────────────────────────────────────────────────────────────────────
    // Logo
    // ─────────────────────────────────────────────────────────────────────────

    private const val LOGO_B64 =
        "iVBORw0KGgoAAAANSUhEUgAAAFoAAAA8CAYAAADmBa1FAAAAIGNIUk0AAHomAACAhAAA+gAAAIDoAAB1" +
        "MAAA6mAAADqYAAAXcJy6UTwAAAAGYktHRAD/AP8A/6C9p5MAAAAHdElNRQfqBQsQFyRBTFkCAAAPK0l" +
        "EQVRo3u3ZaZBdZZkH8P/znuXe2/f2vbfTS3oL6e6EbBCWIGQhAQwkZiCAMFTAQcc4WMiIMEgYBXQS" +
        "E7bREVFEzICCVqEOiDIsAkLYlJA9EIRAd9PpJN3pfb3bOefdnvkwNc7MB2uqIgU1eH/f3/et8z9PPf" +
        "We5wBlZWVlZWVlZWVlZWVlZWVlZWVlZX/R6MM62PbcBKq/BDzwU8BJAiIGUf+N8ht5X0Me/jTMtiTM" +
        "G1PBzGS7PgN75KaP9DO779dGW7rGcPsrw5ias9g54WOcXRThIrKAwxZJh5FxNLIqhLh6LuymDDj06s" +
        "2uudNE6227Ufo9l1vH/2HlPbtxRo3G/V0+Dm1VmHledW2O/FnacdsEcZXVNnTAvQnSnbGgNNzx5eMn" +
        "eKBqFob0l+1majNB7RPLtoHo2HJF/ylzvvYS+oeL+Poui5lz/Vk1p1es7S+Y87XPbRwjhx1HQTBIa1" +
        "FQyPnS/1F/93HP1MvOOylwntyX7d+3IJgH2f1yuXX8KSd89XkoY9CYFs5gvXv5cF5/Q/vuNOGLHUmf" +
        "vuMlnMOh4waCIKBEOiiK5I+Xrhtp4PYHzLBTuOXNG7ZsfH2TmNu41fqkofbPBTkV4KAD0HkQAWwBFj" +
        "HAzYL0EJgIYAKRBSAANwPYScBaMAAwA+QDbg3ISrCZAMgHCQewAdipRCnxGSSKPwbZElikISgCOALAf" +
        "4yF3RoAGv78kQ836NNufBahZcyb4rsv9kTXR473DTfpH6pKONcZl1RB01nFiC/QPmVBQidd//vBZU3v" +
        "Iix8D7126vOHl2+6vevc79ZM+f1P9m/86n0Lb7kDwo2DErWkw4OnkFv5cUaUYeEbINYJ8t9j4S8FwC" +
        "DBDOMQkQbFdoJiJ7JQFWAIkMOgxBFAvMAiRmB/Dch9AyI2wtCfAImnk+N3t9tY9gw4sVMhkjsZ3ARr" +
        "momIwCAmR4Lol4A48qFW9Nb2ETy87QDu/k0v+iflpyMWG5saU33VVfFvt49F5wa+OF/5rgeXNYhKfox" +
        "+sWPRGcSycB8GbJ0Ziz28bdp9U+aOFBZ3dg/Pbli3sScXlJ7hYjdMsX8xbOkREE+QcN4EbAZsTgCrZ" +
        "wFxHpjnMOxUIud1gjPA1vhszEaQe4hgOkFiCpvSAlj7EETsSRjzXZB3M8MaWL4D5L5r3OypZKJvQjg" +
        "vAu5LsHoD2KmFiL8BAhFEnhF/CrDvW+sQR7Tohnt/h0d/dxgtU2h2qRR+rbWuIn7Fx1twpC93fakkL" +
        "2Zr8ymfH5vii7tmp0prepfNPTjH37ueB20dDdOhB0Y+/fArvcn5ly9tejOT9FGMeNMxSWp85eBcsAxb" +
        "WZkmq5wJE8beMmHil1alNwm/5UH4x69m7W6FplGgeq1z4sRqq8wEa46xydwnzaK1Rk+9njWNszHN0G" +
        "YGGxBbv4e1ns4aisPSWlLBnbD+T+AffyVZFKC5ho3XY2T6GStTz7DJbPaO29ElKi/+cIOuSifQ9+tO" +
        "5AvRWhd25uolx2D7WwNtw6OFeY0pYRc1x3YqaZw0jQzuOXX+sim2bz2PIIt+BrM/eFndM/PvTZ37XE" +
        "X41t+fNqfmbWL+2HhBXrrptRVgrvgt2L+FjI3IhJ8iOXkPB4Ob1fChap03lkPTwBGGjXT75Ksuc6Sb" +
        "ObTgYv5qUdjzAhf6fwZFRbL+jziKpkKxhPEGOZTTOLIELRdDcyVLsY9H9wZcKjVyZJOkOE4qt4J0f" +
        "hXpYot+oY6dY77z4QbdcXAYM1c31oaF4rkzpmUgHIHf7+1BOi6wfF6VGBoqLGhxDux+56xFM+Jq9GY" +
        "eRQJDAAzAOjwtrfs+35Ovcje8mNzQ1pxd1lKbQFCMLuzKnZ30wuA4CkQ7wvg3WSZvIy0Ok7FNYPZIjm" +
        "ShTDMU+kANBbGUiSLVRgolUOIOweJZUqqNI+9pI1Y8iUjPgESOFRUQ6lYo5w9AzRootFNYuIt05VKO" +
        "1DREJgHlbSWZfIBkxYMI/XeQXSXM3hUfbtD5QoggkMeykq2trdXY1zmCyckAMxtTGBotRv0HB+759s" +
        "n3vZwP0jmZy/wAk94bEATSgLBof6DwuXtW9//gwjHlrnx3KMKxLVNgpZ7nq8FWwfJ06HATy+IPEeVv" +
        "haWQkLh1wxn9hx02abJihKy/2/vCtoh3LvXJwJB1dxBX/grUtJGs/1NScokobD+TjKPIuLvI+oGwTk6" +
        "wu0M8eGQ7bPI6MjQEq64R1p9Jmt6jsLgE4fAGhKPrIScvQryaJlGH85/uxwP783920Ef1wZJZ9X3EP" +
        "efSorI/P3/tWeLN1/vQ3jGE5efMRXdvzhyZKA6YdCpn3IqDl7a8fddDsz6/gcfN6WTFW7cNf+nhDeN" +
        "XrXGqUvNV7wRm1/k4rdnFYz/bGlV6fOGRn699Ea/W18HqFIgtYhhtfPyrY/6UhTh42Zmu7mysMRQrn" +
        "bRnc+7ddSvB2/wpsB6xO2e8Zyy0x9T0VCCwGVQ4RUjpwg8tTkEOr9TUKkqF9x84efLqgzeCV55RBX" +
        "ZjQFbLyHgeIiYxwSAG4gho0XM5fKIVOD2OqRlCqBmT66Z9sLcOLSVCI+IMEpEFgmIJpCVKhpErKkda" +
        "tykRj++rqKl95F/ablmInDmdImfPNf1fe+7eiYuu8D3TclKWsOegQiFnEVEaZLUTlGSMCCp91x8uKWn" +
        "MtgA8l11TbVXM4f7qx/p+tbHm1neeLKzGUPzkk727hj7rbO1zPVc8H02MPX5qS9p/5126KoI4Vlu2Y" +
        "IZPXPB/Z7bl3YZnNk+7Ofri2tvxle8OfopeO3w6WYSe695/QXZL++OFT9ZaOXmtATJgdiruQoKYI2K" +
        "zz3H4YZcxvuDHHdh7xawPLmiHDXyigpLaTBSkIwQBYYCR8SKEMGiuRNCyaObg4uyriYZc99+YYmz35" +
        "W9/fdsj6hNXkLB1MStRoRWQK4D8OIqhBrSyMYfl/Ec51d7R8zlj6UTPF6+wFeNkbUMpsguNi2X3y3+" +
        "6KAo1FwpyvSH6JCzAmo+f0xh/fiSvElHEnzckZruueAlApKU5SRl7XdLrufmq7VfeefN7vSeEEX/LEV" +
        "RvLbseZO7Rg4s2ZVLDbXnF64Qg6frOq9rAGGWOs8ZcZaVNlwriWwsbvA+2R8cFw2VzCFGQH+wdg1e" +
        "VgaMChMUCZiyZg7pzTkm819F+2tqhfzwzzFXkV722fv8jw4vXilDWYaKAbIwwMV6EKOaR9AnjoyWw0" +
        "qUY2cGhQwNpE8isI6P+aQlz9Z0rYmsyWt5MgSyaQNW6DNGf05foUK6OGf2gp+QLuhieNDwazQ4DnbK" +
        "ByogwOtDky8+qGxrOi2v5DxxJV4fqrxbN8dLForyBw6g6aeVtFKkhVYpWLm4MEyaU9RxGCV9GW1c22" +
        "8s/O5cvjanoQQ4VrNQNGAjhu0c/Gjqqis4mBIjQlZ8IO/rf7Dit7uwz0XbhUsRrmjHmJjG4Y589d/yn" +
        "HVMaxtKrfnPtxFY161IP+RjnJuGnkqiuqkXPvi44QRGV1TMx0NENYc2BGs90H8oVpyNSacuc6O9Xm2" +
        "/8t2IolW0ihvIT4qHxXpUN8vYGIsqnhPhlMeIVUtuzJXCWEPwyhzYjyB5IsT+WueltQKoCIgsyXOw8b" +
        "M5TJbPGE7w9oempQOI8Y82JXQOYx5FthGYYlxa8/Af1a2MY0vJsV1B33PeeUFOBxy6eftRTuKOq6NX" +
        "L2tA+kRn3MplfB9npyIsMClEdBorAwJ7X4O59vefsmRPBuf9+dfNrw21nu1rFnKCIdMJFy+ypGO7owW" +
        "TPEFKpOCBcjL53CD7Zp/fImRMqV6q3xbDSicKDvjXPqVKU1cVwboUJf3jd8uzdgyPFq3QxOAHFID45" +
        "nP+ByhcvRxAhykfn2FIwk0tBwomi0mBvvimmopPCXOkKhKHwlOrMjxWv4SCM2SCaPz4WPGxLwRwbRKl" +
        "SQZ2DMJqOIISn1S5hzfaoFJ2IIExnIK+beKPnpQuW1GFLd+6oK9o5mkUV05cjOOEcxI5dcDgseMuj3" +
        "vF6tFRD9rUj2PIyWuMj/S8dbp3VXbHk2MrmBpGcWo1MbQaJCg/jnV3I9w/Ddx00LzwZI2+3I+jrP5" +
        "BJejfKRHZECHeZDNXFPswThW65Lp22+2QpXKUC2bqvfVKXAn2tw3aoKkFfSQh+IiH4RRnI46xUxzha" +
        "FaXUp1hj68PQXBJJ8zmjzYyYS78QUSijUF0cE/xYTdK5NeHit441+6NALSGj46RN0ig1O+mYDZOD4f" +
        "cTvo10qFbZQMZbmhNbeg6MhvdcNuODvd4BANYzMPEmaibHPxnMb30QfZ1ZcTACTxxikevSEjFP+AmIZ" +
        "ApOZQoml4ORCrAWido61C1cgmhkACN790YJT1wz1DF8v1i2EpmEO6Mg7QlxT3TmD+Te2nK1oAueiH1" +
        "MGmr2PSeUhmOJmHMkP6R3ZZorMDkJJLxgoSWqS8fF+GTIUywJApGIO6RjLh2eVuN3dh0pnCwN11Ym3" +
        "N0heb2OIGQEJQYL0ZkOLLsOolAhna0QO5jEYGXMSQ5MyqVGW78uG9sWaR4ZunvRB9ujAeBs3ov+ZbP" +
        "x0CXJx5d/8amb1I5X7jCxmqyoP5FsqtETuR5QNAnO52GsBQcluJVViE+bgcrmYzDZ1Y1859vSI3v78T" +
        "ObfjJYVYH91sIyd1mLLmYAgcZNe2vZcriLGbvA/zkFNcxYflY9hkeLoKRG+xB2WAa0YVj+72GnNgzX" +
        "AUINGOathgFlgTAIkE1VoGQ5sBbPEgHWApYBYwELRmRQNJZ/axn/a88PvqIBzPhVBNq7FZ23LaepC7" +
        "9yUSlUt1gRn8dVrRDV0+EkKgBXgGIehAP4vguTH0XU2wnOj/fHY+4/N9QkNwfSSs9z8c4jX8RH1Z/" +
        "9K+v41bfBEcC+/X1omlrZVor032ljL7Fw2thPeuQnwY4DYgNSgSWrB13XebYiEbv3yPP/urvtgmuhL" +
        "ePwU9d/ZEN+X4IGgFP/+g7sfPRGNC65Ef3bHsHsFX/bnC/JBZGy8wRRIwhkIYYdx+lMV8bfWHxSc8e" +
        "z23vMcbMbMJkP8PrPv/SRDvl9C/q/LFxzJ1LJBA73DGB0ooSxsRJwYB8AA7R8DKmaFGqrK1E/NYux8" +
        "TzWfeFCXLl67kc+5Pc96P/pez/bifteHMf6i2rBsOgakTBSYf2VZ/5FBFtWVlZWVlZWVlZWVlZWVlZ" +
        "WVlZWVlZWVlZW9v/EfwBAvVfSYdsucgAAACV0RVh0ZGF0ZTpjcmVhdGUAMjAyNi0wNS0xMVQxNTo1N" +
        "zo1MCswMDowMDJiACAAAAAldEVYdGRhdGU6bW9kaWZ5ADIwMjYtMDUtMTFUMTU6NTc6NTArMDA6MMBD" +
        "P7icAAAAKHRFWHRkYXRlOnRpbWVzdGFtcAAyMDI2LTA1LTExVDE2OjIzOjM2KzAwOjAwLI97yQAAAABJ" +
        "RU5ErkJggg=="

    // ─────────────────────────────────────────────────────────────────────────
    // CSS
    // ─────────────────────────────────────────────────────────────────────────

    @Suppress("CssUnusedSymbol")
    private fun css() = """
        *{margin:0;padding:0;box-sizing:border-box}
        body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Arial,sans-serif;
             background:#F5F7FA;color:#1A2B4A;font-size:14px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
        @media print{body{background:#fff}.no-print{display:none}.page-break{page-break-before:always}}
        @page{size:A4;margin:18mm}
        .container{max-width:800px;margin:0 auto;padding:20px}
        .header{display:flex;justify-content:space-between;align-items:center;
                padding:20px;background:#fff;border-radius:12px;margin-bottom:16px;
                border-left:4px solid #2B7FE0;box-shadow:0 1px 4px rgba(0,0,0,.08)}
        .logo{height:50px}
        .doc-title{font-size:20px;font-weight:800;color:#1A2B4A}
        .doc-subtitle{font-size:13px;color:#6B7A99;margin-top:4px}
        .card{background:#fff;border-radius:12px;padding:20px;margin-bottom:16px;
              box-shadow:0 1px 4px rgba(0,0,0,.08)}
        .card-title{font-size:14px;font-weight:700;color:#1A2B4A;margin-bottom:12px;
                    padding-bottom:8px;border-bottom:1px solid #EEF2F8}
        .kpi-row{display:flex;gap:12px;flex-wrap:wrap}
        .kpi{flex:1;min-width:110px;background:#EEF2F8;border-radius:8px;padding:12px;text-align:center}
        .kpi-value{font-size:24px;font-weight:800}
        .kpi-unit{font-size:11px;color:#6B7A99}
        .kpi-label{font-size:11px;color:#6B7A99;margin-top:2px}
        .interpret{background:#F0F6FF;border-left:3px solid #2B7FE0;border-radius:8px;
                   padding:12px;margin-top:12px}
        .interpret-label{font-size:10px;font-weight:700;color:#2B7FE0;letter-spacing:1px;margin-bottom:6px}
        .interpret-text{font-size:13px;line-height:1.6;color:#1A2B4A}
        .site-info{display:grid;grid-template-columns:1fr 1fr;gap:8px}
        .info-row{display:flex;justify-content:space-between;padding:6px 8px;
                  background:#F5F7FA;border-radius:6px}
        .info-label{font-size:12px;color:#6B7A99}
        .info-value{font-size:12px;font-weight:600;color:#1A2B4A}
        .histogram{display:flex;align-items:flex-end;gap:3px;height:120px;margin-top:12px}
        .bar-col{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:flex-end;height:100%}
        .bar{width:100%;border-radius:3px 3px 0 0;background:#2B7FE0;display:flex;
             align-items:flex-start;justify-content:center;min-height:2px}
        .bar-count{font-size:7px;color:#fff;font-weight:700;padding-top:2px}
        .bar-label{font-size:8px;color:#6B7A99;margin-top:3px;text-align:center}
        .polarity-row{display:flex;gap:16px}
        .polarity-box{border-radius:8px;padding:16px;text-align:center;margin-bottom:6px}
        .polarity-neg{background:#2B7FE0;color:#fff}
        .polarity-pos{background:#E63B2E;color:#fff}
        .polarity-count{font-size:28px;font-weight:800}
        .polarity-label{font-size:11px;opacity:.9}
        .polarity-title{font-size:16px;font-weight:700;text-align:center}
        .polarity-pct{font-size:13px;color:#6B7A99;text-align:center;margin-top:4px}
        table{width:100%;border-collapse:collapse;font-size:13px}
        th{background:#EEF2F8;color:#6B7A99;font-weight:600;padding:8px 10px;text-align:left;font-size:11px}
        td{padding:8px 10px;border-bottom:1px solid #F5F7FA;color:#1A2B4A}
        tr:last-child td{border-bottom:none}
        .conclusion{background:#EEF7F0;border-radius:12px;padding:20px;margin-bottom:16px}
        .conclusion-title{font-size:15px;font-weight:800;color:#1A2B4A;margin-bottom:12px}
        .conclusion-text{font-size:14px;line-height:1.7;color:#1A2B4A}
        .norm-badge{display:inline-block;background:#D4EFE0;color:#1A6B3A;font-size:10px;
                    font-weight:700;padding:3px 8px;border-radius:4px;margin-right:6px;margin-top:10px}
        .footer{text-align:center;padding:20px;font-size:11px;color:#9AADC0}
        .footer strong{color:#6B7A99}
        .nsg-value{font-size:56px;font-weight:800;color:#2B7FE0;text-align:center;padding:20px 0 8px}
        .nsg-unit{font-size:14px;color:#6B7A99;text-align:center;margin-bottom:8px}
        .observations{font-size:13px;color:#1A2B4A;line-height:1.6;padding:12px;
                      background:#F5F7FA;border-radius:8px;white-space:pre-wrap}
    """.trimIndent()

    // ─── Cert Foudroiement — HTML Head ──────────────────────────────────────

    private fun certFoudrHtmlHead(lang: PdfOutputLang, documentTitle: String) = """
<!DOCTYPE html>
<html lang="${lang.code}">
<head>
<meta charset="UTF-8">
<title>$documentTitle</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Inter', -apple-system, sans-serif; background: #F5F7FA; color: #1A2B4A; font-size: 15px; }
  .page { width: 210mm; min-height: 297mm; margin: 0 auto 24px; background: #fff; padding: 0 0 72px 0; position: relative; }
  .header { background: #fff; border-bottom: 3px solid #2B7FE0; padding: 18px 32px 14px; display: flex; align-items: center; justify-content: space-between; }
  .header img { height: 52px; max-height: 52px; }
  .header-title { text-align: right; }
  .header-title h1 { font-size: 18px; font-weight: 700; color: #1A2B4A; }
  .header-title p { font-size: 13px; color: #6B7A99; margin-top: 2px; }
  .site-block { text-align: center; padding: 16px 32px 12px; border-bottom: 1px solid #E8EDF5; }
  .site-name { font-size: 22px; font-weight: 700; color: #1A2B4A; }
  .site-meta { font-size: 13px; color: #6B7A99; margin-top: 5px; letter-spacing: 0.05em; text-transform: uppercase; }
  .body { padding: 18px 32px; }
  .section-title { font-size: 12px; font-weight: 600; color: #2B7FE0; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 10px; margin-top: 16px; }
  .section-title:first-child { margin-top: 0; }
  .map-container { width: 100%; height: 210px; border-radius: 10px; border: 1px solid #D0D9E8; overflow: hidden; margin-bottom: 16px; background: #e8eff7; }
  .impact-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 16px; }
  .impact-cell { background: #F5F7FA; border: 1px solid #E0E6F0; border-radius: 8px; padding: 9px 12px; }
  .impact-cell .label { font-size: 11px; color: #6B7A99; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 3px; }
  .impact-cell .value { font-size: 15px; font-weight: 600; color: #1A2B4A; }
  .impact-cell .value.neg { color: #2B7FE0; }
  .impact-cell .value.pos { color: #E63B2E; }
  .impact-cell .value.accent { color: #F5A800; font-size: 17px; }
  .proba-block { background: #FFF3E0; border: 2px solid #F5A800; border-radius: 10px; padding: 20px 28px; margin-bottom: 20px; display: flex; align-items: center; gap: 24px; }
  .proba-block.high { background: #FFEBEE; border-color: #E63B2E; }
  .proba-block.low { background: #E8F5E9; border-color: #4CAF50; }
  .proba-value { font-size: 48px; font-weight: 800; color: #E65100; line-height: 1; white-space: nowrap; }
  .proba-block.high .proba-value { color: #B71C1C; }
  .proba-block.low .proba-value { color: #2E7D32; }
  .proba-label { font-size: 18px; font-weight: 600; color: #1A2B4A; line-height: 1.3; }
  .proba-sublabel { font-size: 13px; color: #6B7A99; margin-top: 3px; }
  .energy-note strong { color: #2B7FE0; }
  .incidence-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px; }
  .incidence-card { border-radius: 10px; padding: 14px 16px; border: 2px solid transparent; }
  .incidence-card.direct { background: #FFF3F3; border-color: #E63B2E; }
  .incidence-card.indirect { background: #FFF8E8; border-color: #F5A800; }
  .incidence-card.active-direct { background: #FFF3F3; border-color: #E63B2E; border-width: 3px; box-shadow: 0 0 0 3px rgba(230,59,46,0.15); }
  .incidence-card.active-indirect { background: #FFF8E8; border-color: #F5A800; border-width: 3px; box-shadow: 0 0 0 3px rgba(245,168,0,0.15); }
  .incidence-badge { display: inline-block; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; padding: 3px 10px; border-radius: 4px; margin-bottom: 8px; }
  .incidence-card.direct .incidence-badge, .incidence-card.active-direct .incidence-badge { background: #E63B2E; color: white; }
  .incidence-card.indirect .incidence-badge, .incidence-card.active-indirect .incidence-badge { background: #F5A800; color: white; }
  .incidence-card h3 { font-size: 14px; font-weight: 700; color: #1A2B4A; margin-bottom: 6px; }
  .incidence-card p { font-size: 13px; color: #2D3748; line-height: 1.5; margin-bottom: 8px; }
  .incidence-seuil { font-size: 12px; font-weight: 600; color: #6B7A99; margin-bottom: 6px; }
  .effect-tags { display: flex; flex-wrap: wrap; gap: 5px; }
  .effect-tag { font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 4px; background: rgba(0,0,0,0.07); color: #1A2B4A; }
  .current-indicator { font-size: 12px; font-weight: 700; color: #F5A800; margin-bottom: 6px; display: flex; align-items: center; gap: 5px; }
  .current-dot { width: 8px; height: 8px; border-radius: 50%; background: #F5A800; display: inline-block; }
  .comment-block { background: #F8F9FC; border: 1px solid #D0D9E8; border-radius: 10px; padding: 14px 18px; margin-bottom: 16px; }
  .comment-block h3 { font-size: 14px; font-weight: 700; color: #1A2B4A; margin-bottom: 8px; }
  .comment-block p { font-size: 14px; color: #2D3748; line-height: 1.6; }
  .comment-block .profile-tags { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 10px; }
  .profile-tag { font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 12px; background: #EEF4FF; color: #2B7FE0; border: 1px solid #C3D9F8; }
  .reco-list { margin-bottom: 16px; }
  .reco-item { display: flex; align-items: flex-start; gap: 10px; padding: 9px 12px; border-radius: 8px; margin-bottom: 6px; border: 1px solid transparent; }
  .reco-item.p1 { background: #FFF3F3; border-color: #F5C6C3; }
  .reco-item.p2 { background: #FFF8E8; border-color: #F5DFA0; }
  .reco-item.conseil { background: #F0FBF4; border-color: #A8D5B5; }
  .reco-priority { font-size: 10px; font-weight: 700; text-transform: uppercase; padding: 2px 7px; border-radius: 3px; white-space: nowrap; margin-top: 2px; }
  .reco-item.p1 .reco-priority { background: #E63B2E; color: white; }
  .reco-item.p2 .reco-priority { background: #F5A800; color: white; }
  .reco-item.conseil .reco-priority { background: #2E7D32; color: white; }
  .reco-text { font-size: 13px; color: #1A2B4A; line-height: 1.5; }
  .seuils-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 16px; }
  .seuil-card { border-radius: 8px; padding: 9px 11px; border: 1px solid transparent; }
  .seuil-card.high { background: #FFF3F3; border-color: #E63B2E; }
  .seuil-card.moderate { background: #FFF8E8; border-color: #F5A800; }
  .seuil-card.low { background: #F0FBF4; border-color: #2E7D32; }
  .seuil-range { font-size: 15px; font-weight: 700; margin-bottom: 3px; }
  .seuil-card.high .seuil-range { color: #E63B2E; }
  .seuil-card.moderate .seuil-range { color: #C07A00; }
  .seuil-card.low .seuil-range { color: #2E7D32; }
  .seuil-label { font-size: 12px; font-weight: 600; color: #1A2B4A; margin-bottom: 3px; }
  .seuil-desc { font-size: 11px; color: #4A5568; line-height: 1.4; }
  .normes { font-size: 12px; color: #8A97B0; text-align: center; padding: 0 20px; line-height: 1.5; }
  .footer { position: absolute; bottom: 24px; left: 32px; right: 32px; border-top: 1px solid #D0D9E8; padding-top: 8px; display: flex; justify-content: space-between; align-items: center; font-size: 11px; color: #8A97B0; }
  .historique-card { border-radius: 10px; padding: 16px 20px; margin: 4px 0 16px; border-left: 5px solid; box-shadow: 0 2px 8px rgba(0,0,0,0.07); }
  .historique-card.no-incident { background: #F0FBF4; border-color: #43A047; }
  .historique-card.has-incident { background: #FFF0F0; border-color: #E63B2E; }
  .hist-titre { font-size: 15px; font-weight: 700; margin-bottom: 6px; color: #1A2B4A; }
  .hist-row { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
  .hist-tag { background: #FDECEA; border: 1px solid #E63B2E; color: #7B1F1A; border-radius: 20px; padding: 3px 12px; font-size: 13px; font-weight: 600; }
  .hist-date { display: inline-block; background: #FDECEA; border: 1px solid #E63B2E; color: #7B1F1A; border-radius: 20px; padding: 2px 12px; font-size: 13px; font-weight: 600; margin-bottom: 6px; }
  .q-body { padding: 28px 40px; }
  .q-intro { font-size: 14px; color: #4A5568; line-height: 1.6; margin-bottom: 24px; padding: 14px 18px; background: #EEF4FF; border-radius: 8px; border-left: 4px solid #2B7FE0; }
  .q-group { margin-bottom: 20px; }
  .q-label { font-size: 13px; font-weight: 600; color: #1A2B4A; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 6px; }
  .q-options { display: flex; flex-wrap: wrap; gap: 8px; }
  .q-option { padding: 8px 16px; border: 1.5px solid #D0D9E8; border-radius: 8px; font-size: 14px; background: #FAFBFD; color: #1A2B4A; }
  .q-option.selected { background: #2B7FE0; color: white; border-color: #2B7FE0; }
  .q-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .q-divider { border: none; border-top: 1px solid #E8EDF5; margin: 8px 0 20px; }
  @media print { body { background: white; } .page { margin: 0; box-shadow: none; } }
</style>
</head>
<body>
"""



    // ─── Lightning certificate — questionnaire page ────────────────────────

    @Suppress("LongParameterList")
    private fun certFoudrPageQ(
        siteName: String,
        siteType: String,
        presence: String,
        hasRod: Boolean,
        hasSurge: Boolean,
        objective: String,
        incident: PdfIncidentData?,
        t: StrikeCertificateTranslations,
    ): String {
        val q = t.questionnaire
        fun opt(label: String, selected: Boolean) =
            """<div class="q-option${if (selected) " selected" else ""}">$label</div>"""

        val siteTypeOptions = SITE_TYPES_FR.joinToString("\n        ") { opt(t.siteType(it), it == siteType) }
        val presenceOptions = PRESENCE_OPTIONS_FR.joinToString("\n        ") { opt(t.presence(it), it == presence) }
        val objectiveOptions = OBJECTIVE_OPTIONS_FR.joinToString("\n        ") { opt(t.objective(it), it == objective) }
        val incidentHtml = if (incident != null) {
            val tagsHtml = if (incident.damageTypes.isNotEmpty()) {
                incident.damageTypes.joinToString("") { """<span class="hist-tag">&#9888; ${t.damageType(it)}</span>""" }
            } else {
                """<span class="hist-tag">${q.unspecifiedDamage}</span>"""
            }
            """
            <div style="margin-top:12px;">
              <div style="font-size:13px;color:#1A2B4A;font-weight:600;margin-bottom:6px;">
                ${q.dateLabel}${incident.date.ifBlank { q.unspecifiedDate }}
              </div>
              <div class="hist-row">$tagsHtml</div>
            </div>"""
        } else ""

        return """
<div class="page">
  <div class="header">
    <img src="data:image/png;base64,$LOGO_B64" alt="Strike Radar">
    <div class="header-title">
      <h1>${t.documentTitle}</h1>
      <p>${q.subtitle}</p>
    </div>
  </div>
  <div class="q-body">
    <div class="q-intro">${q.intro}</div>
    <div class="q-group">
      <div class="q-label">1 &middot; ${q.fieldLabels[0]}</div>
      <div style="padding:10px 14px;border:1.5px solid #2B7FE0;border-radius:8px;font-size:15px;color:#1A2B4A;background:#EEF4FF;font-weight:600;">$siteName</div>
    </div>
    <hr class="q-divider"/>
    <div class="q-group">
      <div class="q-label">2 &middot; ${q.fieldLabels[1]}</div>
      <div class="q-options">$siteTypeOptions</div>
    </div>
    <hr class="q-divider"/>
    <div class="q-group">
      <div class="q-label">3 &middot; ${q.fieldLabels[2]}</div>
      <div class="q-options">$presenceOptions</div>
    </div>
    <hr class="q-divider"/>
    <div class="q-row">
      <div class="q-group">
        <div class="q-label">4 &middot; ${q.fieldLabels[3]}</div>
        <div class="q-options">${opt(q.yes, hasRod)}${opt(q.no, !hasRod)}</div>
      </div>
      <div class="q-group">
        <div class="q-label">5 &middot; ${q.fieldLabels[4]}</div>
        <div class="q-options">${opt(q.yes, hasSurge)}${opt(q.no, !hasSurge)}</div>
      </div>
    </div>
    <hr class="q-divider"/>
    <div class="q-group">
      <div class="q-label">6 &middot; ${q.fieldLabels[5]}</div>
      <div class="q-options">$objectiveOptions</div>
    </div>
    <hr class="q-divider"/>
    <div class="q-group">
      <div class="q-label">7 &middot; ${q.fieldLabels[6]}</div>
      <div class="q-options">
        ${opt(q.noIncident, incident == null)}
        ${opt(q.incidentOccurred, incident != null)}
      </div>$incidentHtml
    </div>
  </div>
</div>
"""
    }


    // ─── Lightning certificate — strike data and probability page ──────────

    @Suppress("LongParameterList")
    private fun certFoudrPage1(
        siteName: String,
        siteMeta: String,
        siteLat: Double,
        siteLon: Double,
        impactLat: Double,
        impactLon: Double,
        distLabel: String,
        externalId: String,
        dateTime: String,
        distFmt: String,
        kaFmt: String,
        polarity: String,
        sensorCount: Int,
        pctFmt: String,
        probaClass: String,
        incident: PdfIncidentData?,
        genDate: String,
        lang: PdfOutputLang,
        t: StrikeCertificateTranslations,
    ): String {
        val p = t.page1
        val polarityClass = if (polarity.contains("+")) "pos" else "neg"
        val sensorLabel = if (sensorCount > 0) "$sensorCount ${p.sensorUnit}" else "—"
        val historiqueHtml = if (incident != null) {
            val tagsHtml = incident.damageTypes.joinToString("") {
                """<span class="hist-tag">&#9888; ${t.damageType(it)}</span>"""
            }
            """
    <div class="historique-card has-incident">
      <div class="hist-titre">&#9889; ${p.incidentReported}</div>
      <div class="hist-date">&#128197; ${incident.date.ifBlank { p.unspecifiedIncidentDate }}</div>
      <div style="font-size:13px;color:#7B1F1A;margin-bottom:8px;">${p.damageTypes}</div>
      <div class="hist-row">$tagsHtml</div>
    </div>"""
        } else {
            """
    <div class="historique-card no-incident">
      <div class="hist-titre">&#10004; ${p.noIncidentReported}</div>
      <div style="font-size:14px;color:#2E7D32;margin-top:2px;">${p.noIncidentDescription}</div>
      <div style="font-size:13px;color:#555;margin-top:6px;font-style:italic;">${p.noIncidentReference}</div>
    </div>"""
        }
        val mapSvg = SvgMapBuilder.impactDistanceMap(
            siteLat = siteLat, siteLon = siteLon,
            impactLat = impactLat, impactLon = impactLon,
            distLabel = distLabel, lang = lang
        )
        val thresholdClasses = listOf("high", "moderate", "low")
        val thresholdHtml = p.thresholds.mapIndexed { index, threshold ->
            """
      <div class="seuil-card ${thresholdClasses[index]}">
        <div class="seuil-range">${threshold.range}</div>
        <div class="seuil-label">${threshold.label}</div>
        <div class="seuil-desc">${threshold.description}</div>
      </div>""".trimIndent()
        }.joinToString("\n")

        return """
<div class="page">
  <div class="header">
    <img src="data:image/png;base64,$LOGO_B64" alt="Strike Radar">
    <div class="header-title">
      <h1>${t.documentTitle}</h1>
      <p>${p.subtitle}</p>
    </div>
  </div>
  <div class="site-block">
    <div class="site-name">$siteName</div>
    <div class="site-meta">$siteMeta</div>
  </div>
  <div class="body">
    <div class="section-title">${p.location}</div>
    <div class="map-container">$mapSvg</div>

    <div class="section-title">${p.impactData}</div>
    <div class="impact-grid">
      <div class="impact-cell"><div class="label">${p.impactFields[0]}</div><div class="value">$externalId</div></div>
      <div class="impact-cell"><div class="label">${p.impactFields[1]}</div><div class="value">$dateTime</div></div>
      <div class="impact-cell"><div class="label">${p.impactFields[2]}</div><div class="value accent">$distFmt</div></div>
      <div class="impact-cell"><div class="label">${p.impactFields[3]}</div><div class="value $polarityClass">$kaFmt kA &nbsp; $polarity</div></div>
      <div class="impact-cell"><div class="label">${p.impactFields[4]}</div><div class="value">$sensorLabel</div></div>
      <div class="impact-cell"><div class="label">${p.impactFields[5]}</div><div class="value">${p.impactType}</div></div>
    </div>

    <div class="section-title">${p.probabilityTitle}</div>
    <div class="proba-block $probaClass">
      <div class="proba-value">$pctFmt %</div>
      <div>
        <div class="proba-label">${p.probabilityLabel}</div>
        <div class="proba-sublabel">${p.probabilityDescription}</div>
      </div>
    </div>

    <div class="section-title">${p.historyTitle}</div>
    $historiqueHtml

    <div class="section-title" style="margin-top:18px;">${p.thresholdTitle}</div>
    <div class="seuils-grid">$thresholdHtml</div>
  </div>
  <div class="footer">
    <span>Strike Radar &mdash; ${p.footerCertificate}</span>
    <span>1 / 2 &middot; ${p.editedOn} $genDate</span>
  </div>
</div>
"""
    }


    // ─── Lightning certificate — evaluation and commentary page ───────────

    @Suppress("LongParameterList", "LongMethod", "CyclomaticComplexMethod")
    private fun certFoudrPage2(
        siteName: String,
        siteMeta: String,
        externalId: String,
        pctFmt: String,
        isDirectActive: Boolean,
        isIndirectActive: Boolean,
        pct: Double,
        siteType: String,
        presence: String,
        hasRod: Boolean,
        hasSurge: Boolean,
        objective: String,
        kaFmt: String,
        polarity: String,
        genDate: String,
        t: StrikeCertificateTranslations,
    ): String {
        val p = t.page2
        val directClass = if (isDirectActive) "active-direct" else "direct"
        val indirectClass = if (isIndirectActive) "active-indirect" else "indirect"
        val currentDirect = if (isDirectActive) """<div class="current-indicator"><span class="current-dot"></span> ${p.currentCase} ($pctFmt %)</div>""" else ""
        val currentIndirect = if (isIndirectActive) """<div class="current-indicator"><span class="current-dot"></span> ${p.currentCase} ($pctFmt %)</div>""" else ""
        val tags = mutableListOf<String>()
        if (siteType.isNotBlank()) tags.add(t.siteType(siteType))
        if (presence.isNotBlank()) tags.add(t.presence(presence))
        tags.add(if (hasRod) p.withLightningRod else p.withoutLightningRod)
        tags.add(if (hasSurge) p.withSurgeProtection else p.withoutSurgeProtection)
        if (objective.isNotBlank()) tags.add(t.objective(objective))
        val profileTagsHtml = tags.joinToString("") { """<span class="profile-tag">$it</span>""" }
        val commentary = buildCertFoudrCommentary(siteType, presence, hasRod, hasSurge, objective, pctFmt, kaFmt, polarity, t)
        val recoHtml = buildCertFoudrRecommendations(pct, hasRod, hasSurge, t)
        val metaP2 = "$siteMeta &middot; ${p.impactPrefix} $externalId &middot; $pctFmt % ${p.incidenceSuffix}"
        val directEffects = p.directEffects
        val indirectEffects = p.indirectEffects

        return """
<div class="page">
  <div class="header">
    <img src="data:image/png;base64,$LOGO_B64" alt="Strike Radar">
    <div class="header-title">
      <h1>${t.documentTitle}</h1>
      <p>${t.page1.subtitle}</p>
    </div>
  </div>
  <div class="site-block">
    <div class="site-name">$siteName</div>
    <div class="site-meta">$metaP2</div>
  </div>
  <div class="body">
    <div class="section-title">${p.incidenceLevels}</div>
    <div class="incidence-grid">
      <div class="incidence-card $directClass">
        $currentDirect
        <span class="incidence-badge">${p.directBadge}</span>
        <h3>${p.directTitle}</h3>
        <div class="incidence-seuil">${p.directThreshold}</div>
        <p>${p.directDescription}</p>
        <div class="effect-tags">
          <span class="effect-tag">&#128293; ${directEffects.first}</span>
          <span class="effect-tag">&#128165; ${directEffects.second}</span>
          <span class="effect-tag">&#9888;&#65039; ${directEffects.third}</span>
          <span class="effect-tag">&#127959;&#65039; ${directEffects.fourth}</span>
        </div>
      </div>
      <div class="incidence-card $indirectClass">
        $currentIndirect
        <span class="incidence-badge">${p.indirectBadge}</span>
        <h3>${p.indirectTitle}</h3>
        <div class="incidence-seuil">${p.indirectThreshold}</div>
        <p>${p.indirectDescription}</p>
        <div class="effect-tags">
          <span class="effect-tag">&#9889; ${indirectEffects.first}</span>
          <span class="effect-tag">&#128268; ${indirectEffects.second}</span>
          <span class="effect-tag">&#128225; ${indirectEffects.third}</span>
          <span class="effect-tag">&#128187; ${indirectEffects.fourth}</span>
        </div>
      </div>
    </div>

    <div class="section-title">${p.commentaryTitle}</div>
    <div class="comment-block">
      <div class="profile-tags">$profileTagsHtml</div>
      <p>$commentary</p>
    </div>

    <div class="section-title">${p.recommendationsTitle}</div>
    <div class="reco-list">$recoHtml</div>
  </div>
  <div class="footer">
    <span>Strike Radar &mdash; ${p.footerCertificate}</span>
    <span>2 / 2 &middot; ${p.editedOn} $genDate</span>
  </div>
</div>
"""
    }

    // ─── Contextual commentary and recommendations ────────────────────────

    @Suppress("LongParameterList")
    private fun buildCertFoudrCommentary(
        siteType: String,
        presence: String,
        hasRod: Boolean,
        hasSurge: Boolean,
        objective: String,
        pctFmt: String,
        kaFmt: String,
        polarity: String,
        t: StrikeCertificateTranslations,
    ): String {
        val c = t.commentary
        val siteDesc = if (siteType.isNotBlank()) c.siteLead + t.siteType(siteType) else c.genericSite
        val protectionKey = (if (hasRod) 1 else 0) + (if (hasSurge) 2 else 0)
        val protectionDesc = c.protectionDescriptions.getValue(protectionKey)
        val effectDesc = if (!hasSurge) c.noSurgeEffect else c.surgeEffect
        val presenceNote = when (presence) {
            "Permanente" -> c.permanentPresence
            "Horaire de travail" -> c.workHoursPresence
            else -> ""
        }
        val objectiveNote = c.objectiveNotes[objective].orEmpty()
        return "$siteDesc $protectionDesc. ${c.riskLead} <strong>$pctFmt %</strong>" +
            "${c.riskIntensityConnector} <strong>$kaFmt kA</strong> ($polarity)$effectDesc$presenceNote$objectiveNote"
    }

    private fun buildCertFoudrRecommendations(
        pct: Double,
        hasRod: Boolean,
        hasSurge: Boolean,
        t: StrikeCertificateTranslations,
    ): String {
        val r = t.recommendations
        val items = mutableListOf<String>()
        if (pct >= 30) {
            items.add(recoItem("p1", r.urgent, r.equipmentCheck))
            items.add(recoItem("p1", r.urgent, r.claimFile))
        }
        if (!hasSurge) items.add(recoItem("p2", r.priority2, r.surgeProtection))
        if (!hasRod && pct >= 80) items.add(recoItem("p2", r.priority2, r.lightningProtectionStudy))
        items.add(recoItem("conseil", r.advice, r.monitoring))
        return items.joinToString("\n")
    }

    private fun recoItem(cls: String, priority: String, text: String) = """
      <div class="reco-item $cls">
        <span class="reco-priority">$priority</span>
        <div class="reco-text">$text</div>
      </div>"""


}